//npm packages
import React, { useEffect, useCallback, memo } from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm, change } from 'redux-form';
import { Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faUnlock } from '@fortawesome/free-solid-svg-icons';
import swal from 'sweetalert2';

//redux actions
import { userForm, USERFORM } from '../../redux/actions';

//elements starts with capital letter;
import {
  InputField,
  FormBase,
  SelectField,
  Validator,
  ButtonGroup
} from '../../components';

//helper files
import strings from '../../globalization';
import utility from '../../utility';
import consts from '../../consts';
import BasicRowColGrid from '../../utility/BasicRowColGrid';

const comboFields = [{ name: 'UserRole', isArray: false }];
const UserForm = memo(props => {
  const { dispatch, initialValues = {}, comboType = {} } = props,
    isReadOnly = initialValues.UserName === 'superadmin',
    { params } = props.match;
  const disableReset = params.id.toLowerCase() === 'new' ? false : true;
  if (disableReset) {
    initialValues.Password = consts.FakePassword;
    initialValues.ConfirmPassword = consts.FakePassword;
  }
  useEffect(() => {
    dispatch(userForm({ action: 'load', _id: params.id, comboType: ['Role'] }));
  }, []);

  const accountStatusChange = buttonName => {
    if (buttonName === strings.Locked) {
      swal.fire(strings.Alert, strings.LockedAlert, 'warning');
    } else {
      props.change('Tries', 0);
    }
  };

  const onSubmit = useCallback(values => {
    const data = utility.processDataBeforeSave(values, comboFields);
    Object.assign(data, { tableName: 'User' });
    if (values.Password !== values.ConfirmPassword) {
      swal.fire({
        title: strings.Info,
        icon: 'info',
        text: strings.ConfirmPasswordAlert
      });
      return false;
    }
    dispatch(
      userForm({
        action: params.id.toLowerCase() === 'new' ? 'add' : 'update',
        ...data
      })
    );
  }, []);
  const roleOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.Role
    ) || [];

  return (
    <FormBase
      {...props}
      isDark={props.isDark}
      onSubmit={onSubmit}
      title={strings.UserForm}
      subTitle={initialValues.UserName}
      actionInfo={{ action: userForm, actionName: USERFORM }}
      disableReset={disableReset}
      historyDisable={params.id.toLowerCase() === 'new'}
    >
      <Row sm="12" md="12" lg="12">
        <BasicRowColGrid>
          {strings.UserName}
          <Field
            name="UserName"
            type="text"
            component={InputField}
            placeholder={strings.UserName}
            disabled={isReadOnly}
            validate={Validator.required}
          />
        </BasicRowColGrid>

        <BasicRowColGrid>
          {strings.FirstName}
          <Field
            name="FirstName"
            type="text"
            component={InputField}
            placeholder={strings.FirstName}
            disabled={isReadOnly}
          />
        </BasicRowColGrid>

        <BasicRowColGrid>
          {strings.LastName}
          <Field
            name="LastName"
            type="text"
            component={InputField}
            placeholder={strings.LastName}
            disabled={isReadOnly}
          />
        </BasicRowColGrid>

        <BasicRowColGrid>
          {strings.Email}
          <Field
            name="Email"
            type="text"
            component={InputField}
            placeholder={strings.Email}
            disabled={isReadOnly}
            validate={[Validator.email, Validator.required]}
          />
        </BasicRowColGrid>

        <BasicRowColGrid>
          {strings.Password}
          <Field
            name="Password"
            type="password"
            component={InputField}
            placeholder={strings.Password}
            validate={[Validator.required, Validator.passwordStrength]}
          />
        </BasicRowColGrid>

        <BasicRowColGrid>
          {strings.ConfirmPassword}
          <Field
            name="ConfirmPassword"
            type="password"
            component={InputField}
            placeholder={strings.ConfirmPassword}
            validate={[
              Validator.required,
              Validator.confirmPassword,
              Validator.passwordStrength
            ]}
          />
        </BasicRowColGrid>

        <BasicRowColGrid>
          {strings.UserRole}
          <Field
            name="UserRole"
            type="number"
            component={SelectField}
            placeholder={strings.UserRole}
            options={roleOptions}
            validate={Validator.required}
          />
        </BasicRowColGrid>

        <BasicRowColGrid>
          {strings.Status}
          <Field
            name="Tries"
            type="button"
            component={ButtonGroup}
            disabled={isReadOnly}
            onClick={accountStatusChange}
            className="accountStatusBtn"
            items={[
              {
                title: strings.Unlocked,
                btnContent: <FontAwesomeIcon icon={faUnlock} />,
                activeCondition: [0, 1, 2, 3, 4]
              },
              {
                title: strings.Locked,
                btnContent: <FontAwesomeIcon icon={faLock} />,
                activeCondition: 5
              }
            ]}
          />
        </BasicRowColGrid>

        <div className="form-group form-check ml-3">
          <Field
            name="IsActive"
            type="checkbox"
            component={InputField}
            placeholder={strings.Active}
          />
          <label className="form-check-label">{strings.Active}</label>
        </div>
      </Row>
    </FormBase>
  );
});

const UserReduxForm = reduxForm({
  form: 'userForm',
  enableReinitialize: true
})(UserForm);

const mapDispatchToProps = dispatch => ({
  change: (field, value) => dispatch(change('userForm', field, value))
});
const mapStateToProps = state => {
  const { userForm } = state.form;
  const { data, success, comboType, info } = state.userFormData.data;
  const { isDark } = state.settingData;
  return {
    initialValues: data,
    comboType: comboType,
    isDark: isDark,
    formValues: userForm ? userForm.values : null,
    shouldGoBack:
      success &&
      (info === 'RecordSavedSuccessfully' ||
        info === 'RecordUpdatedSuccessfully' ||
        info === 'DeleteSuccess'),
    loginUserData: state.loginData.data
  };
};

const UserFormModule = connect(
  mapStateToProps,
  mapDispatchToProps
)(UserReduxForm);

export default props => {
  return <UserFormModule {...props} />;
};
